import { ELEMENT_TYPES } from '../types/elements';
import { parseFreeDsl } from './freeDslParser';
import type { MetamodelContext } from './metamodelContext';

export type DiagnosticSeverity = 'error' | 'warning';

export type Diagnostic = {
  line: number;
  ruleId: 'duplicate-shape' | 'unknown-shape' | 'unknown-type' | 'registry-mismatch';
  message: string;
  severity: DiagnosticSeverity;
};

// Build the set of valid DSL shape-declaration keywords from ELEMENT_TYPES values,
// excluding canvas-only types 'arrow', 'text', and 'database_shape' (internal alias).
// Users type the actual values (e.g. 'erd-class' not 'erd_class'), so use Object.values.
// Add 'database' as a user-facing alias for 'database_shape'.
const EXCLUDED = new Set(['arrow', 'text', 'database_shape']);
const BUILTIN_VALID = new Set<string>([
  ...(Object.values(ELEMENT_TYPES) as string[]).filter(v => !EXCLUDED.has(v)),
  'database',
]);

export function lintFreeDsl(text: string, ctx?: MetamodelContext): Diagnostic[] {
  const ast = parseFreeDsl(text);
  const diags: Diagnostic[] = [];

  const validTypes = (ctx && !ctx.open)
    ? new Set<string>(ctx.keywords.keys())
    : BUILTIN_VALID;

  const seen = new Map<string, number>();
  for (const s of ast.shapes) {
    if (!validTypes.has(s.type)) {
      diags.push({
        line: s.line,
        ruleId: 'unknown-type',
        message: `unknown shape type: ${s.type}`,
        severity: 'error',
      });
    }
    if (seen.has(s.id)) {
      diags.push({
        line: s.line,
        ruleId: 'duplicate-shape',
        message: `duplicate shape id: ${s.id}`,
        severity: 'error',
      });
    } else {
      seen.set(s.id, s.line);
    }
    // registry-mismatch: warn when keyword is linked to a registry and the id
    // doesn't match any known entry code in that registry
    if (ctx?.registryEntries) {
      const kw = ctx.keywords.get(s.type);
      if (kw?.registrySlug) {
        const codes = ctx.registryEntries.get(kw.registrySlug);
        if (codes && !codes.has(s.id)) {
          diags.push({
            line: s.line,
            ruleId: 'registry-mismatch',
            message: `'${s.id}' не найден в реестре '${kw.registrySlug}'`,
            severity: 'warning',
          });
        }
      }
    }
  }

  const known = new Set(ast.shapes.map(s => s.id));
  for (const a of ast.arrows) {
    if (!known.has(a.fromId)) {
      diags.push({
        line: a.line,
        ruleId: 'unknown-shape',
        message: `unknown shape: ${a.fromId}`,
        severity: 'error',
      });
    }
    if (!known.has(a.toId)) {
      diags.push({
        line: a.line,
        ruleId: 'unknown-shape',
        message: `unknown shape: ${a.toId}`,
        severity: 'error',
      });
    }
  }

  return diags;
}
